import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Download, Filter, DollarSign } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

interface DischargedBillingData {
  admission_id: string;
  admission_no: string;
  patient_name: string;
  discharge_date: string;
  invoice_total: number;
  paid_amount: number;
  balance: number;
  payment_status: string;
}

export default function DischargedBilling() {
  const { formatCurrency } = useCurrency();
  const [billingData, setBillingData] = useState<DischargedBillingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    loadDischargedBilling();
  }, []);

  const loadDischargedBilling = async () => {
    try {
      const { data: admissions, error: admError } = await supabase
        .from('admissions')
        .select(`
          id,
          admission_no,
          discharge_date,
          patient:patients(id, name)
        `)
        .eq('status', 'discharged')
        .order('discharge_date', { ascending: false });
      
      if (admError) throw admError;

      const billingPromises = (admissions || []).map(async (adm) => {
        const { data: invoices } = await supabase
          .from('invoices')
          .select('*')
          .eq('reference_id', adm.id)
          .eq('reference_type', 'admission');

        const { data: payments } = await supabase
          .from('payments')
          .select('amount')
          .in('invoice_id', (invoices || []).map(inv => inv.id));

        const invoiceTotal = (invoices || []).reduce((sum, inv) => sum + parseFloat(inv.total_amount || '0'), 0);
        const paidAmount = (payments || []).reduce((sum, pay) => sum + parseFloat(pay.amount || '0'), 0);
        const balance = invoiceTotal - paidAmount;

        return {
          admission_id: adm.id,
          admission_no: adm.admission_no,
          patient_name: (adm.patient as any)?.name || '',
          discharge_date: adm.discharge_date || '',
          invoice_total: invoiceTotal,
          paid_amount: paidAmount,
          balance: balance,
          payment_status: balance > 0 ? 'Pending' : 'Paid',
        };
      });

      const results = await Promise.all(billingPromises);
      setBillingData(results);
    } catch (error) {
      console.error('Failed to load billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const csvData = filteredData.map((item) => ({
      'Admission No': item.admission_no,
      'Patient Name': item.patient_name,
      'Discharge Date': new Date(item.discharge_date).toLocaleDateString(),
      'Invoice Total': item.invoice_total.toFixed(2),
      'Paid Amount': item.paid_amount.toFixed(2),
      'Balance': item.balance.toFixed(2),
      'Status': item.payment_status,
    }));
    
    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Discharged Billing');
    XLSX.writeFile(wb, `discharged-billing-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const filteredData = billingData.filter((item) => {
    if (filters.startDate && new Date(item.discharge_date) < new Date(filters.startDate)) return false;
    if (filters.endDate && new Date(item.discharge_date) > new Date(filters.endDate)) return false;
    return true;
  });

  const totalInvoices = filteredData.reduce((sum, item) => sum + item.invoice_total, 0);
  const totalPaid = filteredData.reduce((sum, item) => sum + item.paid_amount, 0);
  const totalBalance = filteredData.reduce((sum, item) => sum + item.balance, 0);

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discharged Patient Billing</h1>
          <p className="text-gray-600">Summary of billing for discharged patients</p>
        </div>
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          <Download className="w-4 h-4" />
          Export to Excel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-600">Total Invoiced</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">₹{totalInvoices.toFixed(2)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Total Paid</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">₹{totalPaid.toFixed(2)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-red-600" />
            <span className="text-sm text-gray-600">Total Balance</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">₹{totalBalance.toFixed(2)}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discharge Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoice Total</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid Amount</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredData.map((item) => (
              <tr key={item.admission_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.admission_no}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.patient_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {new Date(item.discharge_date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                  ₹{item.invoice_total.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                  ₹{item.paid_amount.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                  ₹{item.balance.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.payment_status === 'Paid' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.payment_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredData.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No billing records found
          </div>
        )}
      </div>
    </div>
  );
}
